import React, { useState } from 'react';
import { Plane, Clock, Users, Shield, CheckCircle, Calculator, ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { mexicoStates, privateFlightFeatures, companyInfo } from '../data/mock';
import { motion } from 'framer-motion';
import { FadeInWhenVisible, StaggerContainer, StaggerItem, CinematicCard } from '../components/CinematicEffects';

const PrivateFlightsPage = () => {
  const [selectedState, setSelectedState] = useState('');
  const [estimatedCost, setEstimatedCost] = useState(0);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    destination: '',
    date: '',
    passengers: 1,
    details: ''
  });
  const [submitted, setSubmitted] = useState(false);

  const pricePerHour = 1700; // USD

  const calculateCost = (stateId) => {
    const state = mexicoStates.find(s => s.id === parseInt(stateId));
    if (state) {
      const cost = state.estimatedHours * pricePerHour;
      setEstimatedCost(cost);
      setFormData({ ...formData, destination: state.name });
    }
  };

  const handleStateChange = (e) => {
    const stateId = e.target.value;
    setSelectedState(stateId);
    calculateCost(stateId);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    console.log('Private flight request:', formData);
    setSubmitted(true);
    setTimeout(() => {
      setSubmitted(false);
      setFormData({
        name: '',
        email: '',
        phone: '',
        destination: '',
        date: '',
        passengers: 1,
        details: ''
      });
      setSelectedState('');
      setEstimatedCost(0);
    }, 4000);
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />

      {/* Hero Section */}
      <section className="relative pt-32 pb-20 bg-gradient-to-br from-blue-900 via-blue-800 to-gray-900 text-white overflow-hidden">
        <div className="absolute inset-0 z-0">
          <div 
            className="ken-burns absolute inset-0"
            style={{
              backgroundImage: 'url(https://customer-assets.emergentagent.com/job_oaxaca-airways-dev/artifacts/1a5qawb6_PHOTO-2026-01-27-08-51-56.jpg)',
              backgroundSize: 'cover',
              backgroundPosition: 'center'
            }}
          />
          <div className="absolute inset-0 bg-gradient-to-br from-blue-900/90 via-blue-800/85 to-gray-900/90"></div>
        </div>
        
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div 
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center max-w-4xl mx-auto"
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="inline-flex items-center space-x-2 bg-white/10 backdrop-blur-sm px-6 py-3 rounded-full mb-8 border border-white/20"
            >
              <Plane className="w-5 h-5 text-amber-400" />
              <span className="text-white font-medium">Cessna 208 Grand Caravan</span>
            </motion.div>
            
            <motion.h1 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.3 }}
              className="text-5xl md:text-6xl font-bold mb-6"
            >
              Vuelos Privados a Toda la República
            </motion.h1>
            <motion.p 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.5 }}
              className="text-xl md:text-2xl text-blue-100 mb-8"
            >
              Vuela a cualquier estado de México con flexibilidad, privacidad y comodidad total
            </motion.p>
            
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6, delay: 0.7 }}
              className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 border border-white/20 max-w-2xl mx-auto"
            >
              <div className="text-center">
                <div className="text-6xl font-bold text-amber-400 mb-2">
                  $1,700 <span className="text-3xl">USD</span>
                </div>
                <div className="text-xl text-blue-100">Por hora de vuelo</div>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Características */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <FadeInWhenVisible>
            <div className="text-center mb-12">
              <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
                ¿Por Qué Elegir Nuestros Vuelos Privados?
              </h2>
              <p className="text-xl text-gray-600">
                Disfruta de una experiencia de vuelo exclusiva y personalizada
              </p>
            </div>
          </FadeInWhenVisible>
          
          <StaggerContainer>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
              {privateFlightFeatures.map((feature, index) => (
                <StaggerItem key={index}>
                  <CinematicCard className="border-0 shadow-xl h-full">
                    <CardContent className="p-8 text-center">
                      <motion.div 
                        whileHover={{ scale: 1.1, rotate: 5 }}
                        transition={{ duration: 0.3 }}
                        className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-blue-900 to-blue-700 rounded-xl mb-6"
                      >
                        <CheckCircle className="w-8 h-8 text-white" />
                      </motion.div>
                      <h3 className="text-xl font-bold text-gray-900 mb-3">{feature.title}</h3>
                      <p className="text-gray-600">{feature.description}</p>
                    </CardContent>
                  </CinematicCard>
                </StaggerItem>
              ))}
            </div>
          </StaggerContainer>
        </div>
      </section>

      {/* Nuestra Flota */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              Nuestra Flota: Cessna 208 Grand Caravan
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Aeronaves modernas, confiables y perfectas para vuelos ejecutivos y privados
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center mb-16">
            <div className="space-y-6">
              <div className="grid grid-cols-2 gap-6">
                <div className="bg-blue-50 rounded-xl p-6">
                  <Users className="w-8 h-8 text-blue-900 mb-3" />
                  <div className="text-3xl font-bold text-blue-900 mb-1">13</div>
                  <div className="text-sm text-gray-600">Pasajeros</div>
                </div>
                <div className="bg-blue-50 rounded-xl p-6">
                  <Clock className="w-8 h-8 text-blue-900 mb-3" />
                  <div className="text-3xl font-bold text-blue-900 mb-1">300+</div>
                  <div className="text-sm text-gray-600">km/h</div>
                </div>
                <div className="bg-blue-50 rounded-xl p-6">
                  <Shield className="w-8 h-8 text-blue-900 mb-3" />
                  <div className="text-3xl font-bold text-blue-900 mb-1">100%</div>
                  <div className="text-sm text-gray-600">Seguridad</div>
                </div>
                <div className="bg-blue-50 rounded-xl p-6">
                  <Plane className="w-8 h-8 text-blue-900 mb-3" />
                  <div className="text-3xl font-bold text-blue-900 mb-1">1,800+</div>
                  <div className="text-sm text-gray-600">km Alcance</div>
                </div>
              </div>

              <Card className="border-0 shadow-xl">
                <CardContent className="p-8">
                  <h3 className="text-2xl font-bold text-gray-900 mb-4">Especificaciones</h3>
                  <ul className="space-y-3">
                    {[
                      'Capacidad: 13 pasajeros + 1 piloto',
                      'Equipaje: Amplio compartimento de carga',
                      'Cabina presurizada y climatizada',
                      'Asientos ejecutivos reclinables',
                      'Sistema de audio y comunicación',
                      'Instrumentación avanzada de navegación'
                    ].map((spec, index) => (
                      <li key={index} className="flex items-center space-x-3">
                        <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0" />
                        <span className="text-gray-700">{spec}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            </div>

            <div className="space-y-6">
              <div className="rounded-2xl overflow-hidden shadow-2xl">
                <img 
                  src="/images/aircraft3.png" 
                  alt="Cessna 208 Grand Caravan de Aerotucán"
                  className="w-full h-auto"
                />
              </div>
              <div className="rounded-2xl overflow-hidden shadow-2xl">
                <img 
                  src="/images/aircraft4.png" 
                  alt="Flota de Aerotucán"
                  className="w-full h-auto"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Calculadora y Formulario */}
      <section className="py-20 bg-gradient-to-br from-blue-50 to-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              Solicita Tu Vuelo Privado
            </h2>
            <p className="text-xl text-gray-600">
              Selecciona tu destino y envíanos tu solicitud. Te contactaremos en breve
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Calculadora */}
            <Card className="border-0 shadow-2xl">
              <div className="bg-gradient-to-br from-blue-900 to-blue-700 p-8 text-white">
                <Calculator className="w-10 h-10 mb-3" />
                <h3 className="text-2xl font-bold">Calcula el Costo de tu Vuelo</h3>
              </div>
              <CardContent className="p-8">
                <div className="mb-8">
                  <Label htmlFor="state-select" className="text-gray-700 font-semibold text-lg mb-3 block">
                    Selecciona tu destino
                  </Label>
                  <select
                    id="state-select"
                    className="w-full px-4 py-4 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-lg"
                    value={selectedState}
                    onChange={handleStateChange}
                  >
                    <option value="">-- Elige un estado --</option>
                    {mexicoStates.map((state) => (
                      <option key={state.id} value={state.id}>
                        {state.name} - {state.capital}
                      </option>
                    ))}
                  </select>
                </div>

                {estimatedCost > 0 && (
                  <div className="bg-gradient-to-br from-amber-500 to-amber-600 rounded-2xl p-8 text-white animate-in slide-in-from-top">
                    <div className="text-center">
                      <div className="text-sm mb-2">Costo estimado del vuelo</div>
                      <div className="text-5xl font-bold mb-4">
                        ${estimatedCost.toLocaleString()} USD
                      </div>
                      <div className="text-sm opacity-90 mb-4">
                        Tiempo estimado: {mexicoStates.find(s => s.id === parseInt(selectedState))?.estimatedHours} horas
                      </div>
                      <div className="text-xs opacity-75 bg-white/10 rounded-lg p-3">
                        *Precio aproximado basado en tiempo de vuelo. El costo final puede variar según condiciones específicas.
                      </div>
                    </div>
                  </div>
                )}

                {!estimatedCost && (
                  <div className="bg-blue-50 rounded-xl p-8 text-center">
                    <Plane className="w-16 h-16 text-blue-900 mx-auto mb-4" />
                    <p className="text-gray-600">
                      Selecciona un destino para calcular el costo aproximado de tu vuelo privado
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Formulario */}
            <Card className="border-0 shadow-2xl">
              <div className="bg-gradient-to-br from-amber-500 to-amber-600 p-8 text-white">
                <h3 className="text-2xl font-bold mb-2">Formulario de Solicitud</h3>
                <p className="text-amber-100">Completa tus datos y te contactaremos</p>
              </div>
              <CardContent className="p-8">
                {submitted ? (
                  <div className="bg-green-50 border-2 border-green-500 rounded-xl p-8 text-center">
                    <CheckCircle className="w-16 h-16 text-green-600 mx-auto mb-4" />
                    <h3 className="text-2xl font-bold text-green-900 mb-2">
                      ¡Solicitud Enviada!
                    </h3>
                    <p className="text-green-700 mb-4">
                      Gracias por tu interés. Nos pondremos en contacto contigo en las próximas 24 horas para coordinar los detalles de tu vuelo privado.
                    </p>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit} className="space-y-6">
                    <div>
                      <Label htmlFor="name" className="text-gray-700 font-semibold">
                        Nombre Completo *
                      </Label>
                      <Input
                        id="name"
                        name="name"
                        type="text"
                        placeholder="Tu nombre"
                        value={formData.name}
                        onChange={handleChange}
                        required
                        className="mt-2"
                      />
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <Label htmlFor="email" className="text-gray-700 font-semibold">
                          Correo Electrónico *
                        </Label>
                        <Input
                          id="email"
                          name="email"
                          type="email"
                          placeholder="tu@email.com"
                          value={formData.email}
                          onChange={handleChange}
                          required
                          className="mt-2"
                        />
                      </div>

                      <div>
                        <Label htmlFor="phone" className="text-gray-700 font-semibold">
                          Teléfono *
                        </Label>
                        <Input
                          id="phone"
                          name="phone"
                          type="tel"
                          placeholder="(951) 000-0000"
                          value={formData.phone}
                          onChange={handleChange}
                          required
                          className="mt-2"
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <Label htmlFor="date" className="text-gray-700 font-semibold">
                          Fecha Preferida *
                        </Label>
                        <Input
                          id="date"
                          name="date"
                          type="date"
                          value={formData.date}
                          onChange={handleChange}
                          min={new Date().toISOString().split('T')[0]}
                          required
                          className="mt-2"
                        />
                      </div>

                      <div>
                        <Label htmlFor="passengers" className="text-gray-700 font-semibold">
                          Número de Pasajeros *
                        </Label>
                        <select
                          id="passengers"
                          name="passengers"
                          value={formData.passengers}
                          onChange={handleChange}
                          required
                          className="w-full mt-2 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        >
                          {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13].map(num => (
                            <option key={num} value={num}>{num} {num === 1 ? 'pasajero' : 'pasajeros'}</option>
                          ))}
                        </select>
                      </div>
                    </div>

                    <div>
                      <Label htmlFor="details" className="text-gray-700 font-semibold">
                        Detalles Adicionales
                      </Label>
                      <Textarea
                        id="details"
                        name="details"
                        placeholder="Cuéntanos más sobre tu viaje: propósito, equipaje especial, necesidades específicas..."
                        value={formData.details}
                        onChange={handleChange}
                        rows={4}
                        className="mt-2"
                      />
                    </div>

                    <Button
                      type="submit"
                      size="lg"
                      className="w-full bg-gradient-to-r from-blue-900 to-blue-700 hover:from-blue-800 hover:to-blue-600 text-white text-lg py-6"
                    >
                      Enviar Solicitud
                      <ArrowRight className="w-5 h-5 ml-2" />
                    </Button>

                    <p className="text-sm text-gray-500 text-center">
                      Al enviar esta solicitud, aceptas que nos pongamos en contacto contigo para coordinar los detalles del vuelo.
                    </p>
                  </form>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* CTA de Contacto */}
      <section className="py-20 bg-gradient-to-br from-blue-900 via-blue-800 to-gray-900 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">
            ¿Prefieres hablar directamente con nosotros?
          </h2>
          <p className="text-xl text-blue-100 mb-8">
            Nuestro equipo está disponible para responder todas tus preguntas sobre vuelos privados
          </p>
          <div className="flex flex-col sm:flex-row justify-center items-center space-y-4 sm:space-y-0 sm:space-x-6">
            <a href={`tel:${companyInfo.phone}`}>
              <Button
                size="lg"
                className="bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-white text-lg px-10 py-6"
              >
                Llamar: {companyInfo.phone}
              </Button>
            </a>
            <a href="/contacto">
              <Button
                size="lg"
                variant="outline"
                className="bg-white/10 backdrop-blur-sm border-2 border-white text-white hover:bg-white hover:text-blue-900 text-lg px-10 py-6"
              >
                Formulario de Contacto
              </Button>
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default PrivateFlightsPage;
